import { computed, ref, onMounted } from 'vue'
import Modal from '../../../../../../Frontend/src/components/Modal/Modal.vue'
import mapNewLoanUrlParams from '../js/mapNewLoanUrlParams'
import { getTableHeaders, getTableRows } from './mapTableData'
import apiGetCalculatorData from './apiGetCalculatorData'
import NewLoanCalculatorResultDesktop from './NewLoanCalculatorResultDesktop/NewLoanCalculatorResultDesktop.vue'
import NewLoanCalculatorResultMobile from './NewLoanCalculatorResultMobile/NewLoanCalculatorResultMobile.vue'
import { registerGtmDataLayer } from '../../../../Video/gtmDataLayerService'
import AdjustLoanContent from "./ModalContent/AdjustLoanContent/AdjustLoanContent.vue"
import SwitchLoanTypeContent from "./ModalContent/SwitchLoanTypeContent/SwitchLoanTypeContent.vue"
import Loader from '../../../../../../Frontend/src/components/Loader/Loader.vue'

export default {
    name: 'NewLoanCalculatorResult',
    components: {
        Loader,
        NewLoanCalculatorResultDesktop,
        NewLoanCalculatorResultMobile,
        AdjustLoanContent,
        SwitchLoanTypeContent,
        Modal,
    },
    props: {
        labels: {
            type: Object,
            required: true,
        },
        apiUrl: {
            type: String,
            required: true,
        },
        blockId: {
            type: String,
            required: true,
        },
        tableGroups: {
            type: Array,
            required: true,
            default: [],
        },
        housingTypes: {
            type: Array,
            required: true,
            default: [],
        },
        initData: {
            type: Object,
            required: true,
        },
        summary: {
            type: Object,
            required: true,
        },
        showSendCalculation: {
            type: Boolean,
            default: false
        },
        infoBox: {
            type: Object,
            required: true,
        },
        sendCalculation: {
            type: Object,
            required: false,
        },
        links: {
            type: Array,
            required: true,
        }
    },
    setup(props) {
        const showAdjustLoanModal = ref(false);
        const showSwitchLoanTypeModal = ref(false);
        const adjustLoanProps = ref({});
        const switchLoanTypeProps = ref({});
        const modalTitle = ref('');
        const modalSubTitle = ref('');

        // DATA FOR RENDERING
        const infoBox = ref([{ ...props.infoBox }])
        const summaries = ref([{ ...props.summary }])
        const tables = ref([[...props.tableGroups]])
        const calculationData = ref([{ ...props.initData }])
        const showSendCalculation = ref(false)
        const isLoading = ref(false)

        const modalOpen = computed(() => showAdjustLoanModal.value || showSwitchLoanTypeModal.value)

        const getLoanTypesForSummary = (index) => {
            const currentType = props.housingTypes.find((item) => item.keyName === calculationData.value[index].housingType)
            return currentType?.loanTypes ?? []
        }

        function AddCalculationToCookie() {
            var cookieName = "newLoanCalculation";
            var cookieValue = window.location.href;
            var expires = "; expires=" + new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();

            document.cookie = cookieName + "=" + cookieValue + expires + "; path=/; SameSite=Lax";
        }

        onMounted(() => {
            AddCalculationToCookie()
        })

        // SET CALCULATION DATA
        const setCalculationData = (data, index) => {
            if (data.loanType) calculationData.value[index].loanType = data.loanType
            if (data.housePrice) calculationData.value[index].housePrice = parseFloat(data.housePrice)
            if (data.ownPayment) calculationData.value[index].ownPayment = parseFloat(data.ownPayment)
            if (data.housingType) calculationData.value[index].housingType = data.housingType
            if (data.mortgageLoanInterestOnlyLength)
                calculationData.value[index].mortgageLoanInterestOnlyLength = parseFloat(data.mortgageLoanInterestOnlyLength)
            if (data.mortgageLoanTenureLength)
                calculationData.value[index].mortgageLoanTenureLength = parseFloat(data.mortgageLoanTenureLength)
            if (data.bankLoanTenureLength) calculationData.value[index].bankLoanTenureLength = parseFloat(data.bankLoanTenureLength)
        }

        const setResponseData = (response, index) => {
            if (index !== null) {
                summaries.value[index] = { ...response.summary }
                tables.value[index] = [...response.tableGroups]
                calculationData.value[index] = { ...response.initData }
                infoBox.value[index] = { ...response.infoBox }
            } else {
                summaries.value.push({ ...response.summary })
                tables.value.push([...response.tableGroups])
                calculationData.value.push({ ...response.initData })
                infoBox.value.push({ ...response.infoBox })
            }
        }

        const redirect = () => {
            const rootPath = window.location.href.replace(window.location.search, '')
            const params = mapNewLoanUrlParams({
                housePrice: calculationData.value[0].housePrice,
                ownPayment: calculationData.value[0].ownPayment,
                housingType: calculationData.value[0].housingType,
                mortgageLoanInterestOnlyLength: calculationData.value[0].mortgageLoanInterestOnlyLength,
                mortgageLoanTenureLength: calculationData.value[0].mortgageLoanTenureLength,
                bankLoanTenureLength: calculationData.value[0].bankLoanTenureLength,
                productType: calculationData.value[0].loanType,
            })
            window.location.href = rootPath + params
        }

        // HANDLE MODALS
        // SwitchLoanTypeContent
        const getSwitchLoanTypeProps = (index, callback) => ({
            currentHousingType: getLoanTypesForSummary(index),
            labels: props.labels,
            index: index,
            selectedLoanType: calculationData.value[index].loanType,
            onCallback: callback,
        })

        const onSwitchLoanType = async (loanType, currentIndex) => {
            setCalculationData({ loanType }, currentIndex)

            const loadingTimeout = setTimeout(() => {
                isLoading.value = true
            }, 100)

            const response = await apiGetCalculatorData(calculationData.value[currentIndex], props.apiUrl, props.blockId)
            setResponseData(response, currentIndex)

            showSwitchLoanTypeModal.value = false

            clearTimeout(loadingTimeout)
            isLoading.value = false
        }

        const onSwitchLoanTypeWithRedirect = (loanType, currentIndex) => {
            setCalculationData({ loanType }, currentIndex)

            const loadingTimeout = setTimeout(() => {
                isLoading.value = true
            }, 100)
            setTimeout(() => redirect(), 50)
        }

        const switchLoanType = function (index) {
            showSwitchLoanTypeModal.value = true;
            switchLoanTypeProps.value = getSwitchLoanTypeProps(index, onSwitchLoanType);
            modalTitle.value = props.labels.loanTypesTitle;
            modalSubTitle.value = '';
        }

        const switchLoanTypeWithRedirect = function (index) {
            showSwitchLoanTypeModal.value = true;
            switchLoanTypeProps.value = getSwitchLoanTypeProps(index, onSwitchLoanTypeWithRedirect);
            modalTitle.value = props.labels.loanTypesTitle;
            modalSubTitle.value = '';
        }

        // AdjustLoanContent
        const getAdjustLoanProps = (index, callback) => {
            const housingTypesClone = [...props.housingTypes]
            housingTypesClone.forEach((item) => item.selected = false)  // Reset selected

            return {
                labels: props.labels,
                housingTypes: housingTypesClone,
                showHousingTypes: calculationData.value.length === 1,
                index: index,
                initData: calculationData.value[index],
                onCallback: callback
            }
        }

        const onAdjustLoan = async (data, currentIndex) => {
            setCalculationData(data, currentIndex)

            const loadingTimeout = setTimeout(() => {
                isLoading.value = true
            }, 100)

            const response = await apiGetCalculatorData(calculationData.value[currentIndex], props.apiUrl, props.blockId)

            setResponseData(response, currentIndex)

            clearTimeout(loadingTimeout)
            isLoading.value = false
        }

        const onAdjustLoanWithRedirect = function (data) {
            setCalculationData(data, 0)
            const loadingTimeout = setTimeout(() => {
                isLoading.value = true
            }, 100)
            setTimeout(() => redirect(), 50)
        }

        const adjustLoan = function (index) {
            showAdjustLoanModal.value = true;
            adjustLoanProps.value = getAdjustLoanProps(index, onAdjustLoan);
            modalTitle.value = props.labels.adjustLoanModalTitle;
            modalSubTitle.value = summaries.value[index].title;
        }

        const adjustLoanWithRedirect = function (index) {
            showAdjustLoanModal.value = true;
            adjustLoanProps.value = getAdjustLoanProps(index, onAdjustLoanWithRedirect);
            modalTitle.value = props.labels.adjustLoanModalTitle;
            modalSubTitle.value = summaries.value[index].title;
        }

        const openCompareLoanModal = async function (e) {
            e.target.disabled = true

            const clone = { ...calculationData.value[0] }
            const houseType = props.housingTypes.find((item) => item.keyName === clone.housingType)
            const loanType = houseType?.loanTypes.find((item) => item.type === clone.loanType)

            clone.loanType = clone.loanType = loanType?.defaultCompareLoanType ?? props.initData?.defaultCompareLoanType
            clone.mortgageLoanInterestOnlyLength = houseType?.defaultMortgageLoanInterestOnlyLength ?? 0
            clone.mortgageLoanTenureLength = houseType?.defaultMortgageLoanTenureLength ?? 0
            clone.bankLoanTenureLength = houseType?.defaultBankLoanTenureLength ?? 0

            const loadingTimeout = setTimeout(() => {
                isLoading.value = true
            }, 100)

            const response = await apiGetCalculatorData(clone, props.apiUrl, props.blockId)
            setResponseData(response, null)

            clearTimeout(loadingTimeout)
            isLoading.value = false

            setTimeout(() => {
                e.target.disabled = false
            }, 500)

            // close send calculation component
            showSendCalculation.value = false

            window.scrollTo(0, 0)

            // Google Tag Manager
            registerGtmDataLayer({
                event: 'track-event.click_sammenlignlaan',
                eventAction: 'click',
                eventCategory: 'beregner',
                eventLabel: 'sammenlign_laan',
                event_source: '-277',
            })
        }

        const quitCompareMode = function () {
            summaries.value.pop()
            tables.value.pop()
            calculationData.value.pop()
        }

        const openSendCalculationsModal = function () {
            showSendCalculation.value = !showSendCalculation.value

            if (showSendCalculation.value) {
                setTimeout(() => document.querySelector('.nyk-js--send-calc-close-button')?.focus(), 300)
            }
        }

        const closeSendCalculationModal = () => {
            showSendCalculation.value = false
            document.querySelector('.nyk-js--send-calc')?.focus()
        }

        const closeSwitchLoanTypeModal = () => {
            showSwitchLoanTypeModal.value = false
        }

        const closeAdjustLoanModal = () => {
            showAdjustLoanModal.value = false
        }

        const closeModal = () => {
            if (showAdjustLoanModal.value) {
                closeAdjustLoanModal()
            } else if (showSwitchLoanTypeModal.value) {
                closeSwitchLoanTypeModal()
            }
        }

        return {
            infoBox,
            summaries,
            tables,
            showSendCalculation,
            isLoading,
            modalOpen,
            switchLoanTypeProps,
            adjustLoanProps,
            showAdjustLoanModal,
            showSwitchLoanTypeModal,
            modalTitle,
            modalSubTitle,
            closeModal,
            getLoanTypesForSummary,
            getTableHeaders,
            getTableRows,
            switchLoanType,
            switchLoanTypeWithRedirect,
            openSendCalculationsModal,
            openCompareLoanModal,
            adjustLoan,
            adjustLoanWithRedirect,
            quitCompareMode,
            closeSendCalculationModal,
            closeSwitchLoanTypeModal,
            closeAdjustLoanModal,
        }
    },
}